import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { socketService } from '../../services/socketService';
import LoadingState from '../common/LoadingState';
import { 
  ChartBarIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';

interface VotingHistoryProps {
  assemblyId: string;
  userRole: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
  openVotingSessionId?: string | null;
  onVotingOpened?: () => void;
}

interface VotingSession {
  id: string;
  question: string;
  description: string;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  type: string;
  startedAt: string;
  endedAt: string;
  createdAt: string;
  totalVotes: number;
  totalCoefficient?: number;
  optionSummaries?: Array<{
    optionId: string;
    optionText: string;
    votes: number;
    percentage: number;
    coefficient?: number;
    percentageByCoefficient?: number;
  }>;
  totalEligibleVoters: number;
  totalConnectedParticipants: number;
  results: any;
  agendaItem: {
    title: string;
  };
}

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';

const MIN_LOADING_MS = 250;

const ensureMinimumLoadingTime = async (startTime: number) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
  }
};

const VotingHistory: React.FC<VotingHistoryProps> = ({ assemblyId, userRole, openVotingSessionId, onVotingOpened }) => {
  const [votingSessions, setVotingSessions] = useState<VotingSession[]>([]);
  const [hasRequestedLoad, setHasRequestedLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<VotingSession | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showAllSessions] = useState(false); // Para debugging
  const [, setRefreshTrigger] = useState(0); // Para actualizar fechas en tiempo real
  const [votedSessionIds, setVotedSessionIds] = useState<Set<string>>(new Set());

  const mapEndedResultsToOptionSummaries = (endedData: any): VotingSession['optionSummaries'] => {
    const rawResults = endedData?.results?.results;
    if (!Array.isArray(rawResults) || rawResults.length === 0) {
      return [];
    }

    return rawResults.map((result: any, idx: number) => ({
      optionId: String(result?.option ?? `option-${idx}`),
      optionText: String(result?.option ?? `Opción ${idx + 1}`),
      votes: Number(result?.votes || 0),
      percentage: Number(result?.percentage || 0),
      coefficient: Number(result?.coefficient || 0),
      percentageByCoefficient: Number(result?.percentageByCoefficient || result?.percentage || 0),
    }));
  };

  useEffect(() => {
    // Cargar solo cuando el usuario solicite historial (o llegue desde notificación)
    if ((userRole === 'admin' || userRole === 'owner') && hasRequestedLoad) {
      loadVotingHistory();
    }
  }, [assemblyId, userRole, hasRequestedLoad]);

  useEffect(() => {
    const checkUserVotes = async () => {
      if (!hasRequestedLoad || votingSessions.length === 0) {
        setVotedSessionIds(new Set());
        return;
      }

      try {
        const sessionIds = votingSessions.map(session => session.id);
        const response = await apiService.getUserVotedStatusBatch(sessionIds);
        const statusBySession = response.data?.data || {};
        const votedSet = new Set<string>(
          Object.entries(statusBySession)
            .filter(([, hasVoted]) => Boolean(hasVoted))
            .map(([sessionId]) => sessionId)
        );
        setVotedSessionIds(votedSet);
      } catch (error) {
        console.error('Error al consultar votos en batch:', error);
      }
    };

    checkUserVotes();
  }, [hasRequestedLoad, votingSessions]);

  useEffect(() => {
    if (openVotingSessionId) {
      setHasRequestedLoad(true);
    }
  }, [openVotingSessionId]);

  // ✅ Abrir votación si viene de alerta de notificación
  useEffect(() => {
    if (openVotingSessionId && votingSessions.length > 0) {
      const session = votingSessions.find(s => s.id === openVotingSessionId);
      if (session) {
        handleViewDetails(session);
        if (onVotingOpened) {
          onVotingOpened();
        }
      }
    }
  }, [openVotingSessionId, votingSessions]);

  // 🔴 Escuchar eventos de Socket.IO en tiempo real
  useEffect(() => {
    if (!hasRequestedLoad) {
      return;
    }

    const handleVotingSessionCreated = (data: any) => {
      console.log('🆕 Nueva votación creada:', data);
      // Recargar el historial cuando se crea una nueva votación
      loadVotingHistory();
    };

    const handleVotingSessionStarted = (data: any) => {
      console.log('▶️ Votación iniciada:', data);
      // Actualizar la sesión en el listado
      setVotingSessions(prev =>
        prev.map(session =>
          session.id === data.id
            ? { ...session, status: 'active', startedAt: data.startedAt }
            : session
        )
      );
    };

    const handleVotingSessionEnded = (data: any) => {
      console.log('⏹️ Votación finalizada:', data);

      const endedSession = data?.votingSession || data;
      const endedSessionId = endedSession?.id;
      const endedAt = endedSession?.endedAt || data?.endedAt;
      const endedTotalVotes = Number(data?.results?.totalVotes || 0);
      const endedOptionSummaries = mapEndedResultsToOptionSummaries(data) || [];

      if (endedSessionId) {
        // Reflejar estado final inmediatamente y aplicar resumen final incremental.
        let sessionFound = false;
        setVotingSessions(prev =>
          prev.map(session => {
            if (session.id !== endedSessionId) return session;
            sessionFound = true;
            return {
              ...session,
              status: 'completed',
              endedAt,
              totalVotes: endedTotalVotes || session.totalVotes || 0,
              optionSummaries: endedOptionSummaries.length > 0 ? endedOptionSummaries : session.optionSummaries,
            };
          })
        );

        // Fallback: si la sesión no estaba en memoria, recargamos historial una vez.
        if (!sessionFound) {
          loadVotingHistory();
        }
        return;
      }

      // Fallback defensivo cuando el payload no trae id esperado.
      loadVotingHistory();
    };

    const handleVotingSessionCancelled = (data: any) => {
      console.log('❌ Votación cancelada:', data);
      // Actualizar o remover la sesión
      setVotingSessions(prev =>
        prev.map(session =>
          session.id === data.id
            ? { ...session, status: 'cancelled' }
            : session
        )
      );
    };

    const handleVotingSessionDeleted = (data: any) => {
      console.log('🗑️ Votación eliminada:', data);
      const deletedId = data?.id || data?.votingSessionId;
      if (!deletedId) return;

      setVotingSessions(prev => prev.filter(session => session.id !== deletedId));
      setSelectedSession(prev => (prev?.id === deletedId ? null : prev));
    };

    const handleVotingVoteCast = (data: any) => {
      console.log('🗳️ Voto registrado:', data);
      const votedSessionId = data?.votingSessionId || data?.sessionId;
      if (!votedSessionId) return;

      // Evita recarga completa por cada voto; actualiza contador localmente.
      setVotingSessions(prev =>
        prev.map(session =>
          session.id === votedSessionId
            ? { ...session, totalVotes: (session.totalVotes || 0) + 1 }
            : session
        )
      );
    };

    const handleVotingSessionUpdated = (data: any) => {
      console.log('🔄 Votación actualizada:', data);
      // Extraer la sesión del evento
      const updatedSession = data.votingSession || data;
      
      // Actualizar la sesión en el listado
      setVotingSessions(prev => {
        const index = prev.findIndex(s => s.id === updatedSession.id);
        if (index >= 0) {
          // Si la sesión existe, actualizarla
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            ...updatedSession,
            question: updatedSession.question || updated[index].question,
            description: updatedSession.description || updated[index].description,
            status: updatedSession.status || updated[index].status,
            startedAt: updatedSession.startedAt || updated[index].startedAt,
            endedAt: updatedSession.endedAt || updated[index].endedAt
          };
          return updated;
        }
        return prev;
      });
    };

    // Registrar listeners
    socketService.on('voting:session-created', handleVotingSessionCreated);
    socketService.on('voting:session-started', handleVotingSessionStarted);
    socketService.on('voting:session-updated', handleVotingSessionUpdated);
    socketService.on('voting:session-ended', handleVotingSessionEnded);
    socketService.on('voting:session-cancelled', handleVotingSessionCancelled);
    socketService.on('voting:session-deleted', handleVotingSessionDeleted);
    socketService.on('voting:vote-cast', handleVotingVoteCast);

    // Cleanup
    return () => {
      socketService.off('voting:session-created', handleVotingSessionCreated);
      socketService.off('voting:session-started', handleVotingSessionStarted);
      socketService.off('voting:session-updated', handleVotingSessionUpdated);
      socketService.off('voting:session-ended', handleVotingSessionEnded);
      socketService.off('voting:session-cancelled', handleVotingSessionCancelled);
      socketService.off('voting:session-deleted', handleVotingSessionDeleted);
      socketService.off('voting:vote-cast', handleVotingVoteCast);
    };
  }, [hasRequestedLoad]);

  // ⏰ Actualizar fechas relativas en tiempo real cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, []);

  // Verificar permisos
  if (userRole !== 'admin' && userRole !== 'owner') {
    return (
      <div className="text-center py-8">
        <div className={`${shellCardClassName} border-yellow-200 bg-yellow-50 p-4`}>
          <p className="text-yellow-800">
            El historial de votaciones solo está disponible para administradores y propietarios.
          </p>
        </div>
      </div>
    );
  }

  const loadVotingHistory = async () => {
    const startTime = Date.now();
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getVotingSessionsHistoryView(assemblyId);
      
      console.log('🔍 [VotingHistory] Respuesta completa del backend:', response);
      console.log('🔍 [VotingHistory] Datos de sesiones:', response.data);
      
      // Verificar diferentes posibles estructuras de respuesta
      const allSessions = response.data.data || response.data.sessions || response.data || [];
      console.log('🔍 [VotingHistory] Todas las sesiones:', allSessions);
      
      // Filtrar sesiones según el modo y rol
      let filteredSessions;
      if (showAllSessions) {
        // Mostrar todas las sesiones para debugging (solo admin)
        filteredSessions = allSessions;
        console.log('🔍 [VotingHistory] Mostrando TODAS las sesiones para debugging');
      } else if (userRole === 'owner') {
        // Propietarios ven todas las votaciones (incluyendo draft y active)
        filteredSessions = allSessions;
        console.log('🔍 [VotingHistory] Mostrando todas las votaciones para propietario');
      } else {
        // Admin en modo normal: ver todas las votaciones (incluyendo draft y active)
        filteredSessions = allSessions;
        console.log('🔍 [VotingHistory] Mostrando todas las votaciones para admin');
      }
      
      console.log('🔍 [VotingHistory] Sesiones filtradas:', filteredSessions);
      
      // Ordenar por fecha de creación más reciente (para que las nuevas aparezcan primero)
      filteredSessions.sort((a: VotingSession, b: VotingSession) => 
        new Date(b.createdAt || b.startedAt).getTime() - new Date(a.createdAt || a.startedAt).getTime()
      );
      
      setVotingSessions(filteredSessions);
    } catch (err) {
      console.error('Error loading voting history:', err);
      setError('Error al cargar el historial de votaciones');
    } finally {
      await ensureMinimumLoadingTime(startTime);
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'active':
        return <PlayCircleIcon className="h-5 w-5 text-blue-600" />;
      case 'draft':
        return <DocumentTextIcon className="h-5 w-5 text-amber-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      case 'active':
        return 'En curso';
      case 'draft':
        return 'Pendiente';
      default:
        return 'Desconocido';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    let date = new Date(dateString);
    const now = new Date();
    
    if (Number.isNaN(date.getTime())) {
      return now.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    // Calcular diferencia en milisegundos (negativo = pasado, positivo = futuro)
    let diffMs = date.getTime() - now.getTime();
    
    // HEURÍSTICA: Si está muy en el futuro (>1 hora), probablemente es un error de timezone
    // PostgreSQL está guardando con timezone del servidor en lugar de UTC puro
    // Restar la diferencia de timezone de forma aproximada
    if (diffMs > 60 * 60 * 1000) {
      // Parece estar ~5-6 horas en el futuro, probablemente error de timezone
      // Asumir que se creó hace pocos minutos/segundos y corregir
      const approximateMinusOffset = new Date(date.getTime() - (5 * 60 * 60 * 1000));
      const adjustedDiff = approximateMinusOffset.getTime() - now.getTime();
      
      // Solo aplicar la corrección si esto lo pone en el pasado reciente
      if (adjustedDiff < 0 && Math.abs(adjustedDiff) < 10 * 60 * 1000) {
        date = approximateMinusOffset;
        diffMs = adjustedDiff;
      }
    }
    
    const isFuture = diffMs > 0;
    const absDiffMs = Math.abs(diffMs);
    
    const diffMinutes = Math.floor(absDiffMs / (1000 * 60));
    const diffHours = Math.floor(absDiffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    
    // Formato: "Hace X minutos" o "En X minutos"
    if (diffMinutes < 60) {
      if (diffMinutes === 0) {
        return isFuture ? 'En menos de un minuto' : 'Hace menos de un minuto';
      }
      return isFuture
        ? `En ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`
        : `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return isFuture
        ? `En ${diffHours} hora${diffHours !== 1 ? 's' : ''}`
        : `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return isFuture
        ? `En ${diffDays} día${diffDays !== 1 ? 's' : ''}`
        : `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Obtener la fecha relevante según el estado
  const getRelevantDate = (session: VotingSession): string => {
    // Si completada, mostrar desde cuándo finalizó
    if (session.status === 'completed' && session.endedAt) {
      return session.endedAt;
    }
    // Si activa, mostrar desde cuándo comenzó
    if (session.status === 'active' && session.startedAt) {
      return session.startedAt;
    }
    // Si draft o sin iniciarse, mostrar desde cuándo se creó
    return session.createdAt;
  };

  const handleViewDetails = async (session: VotingSession) => {
    setLoadingDetails(true);
    try {
      // Cargar resultados detallados de la votación
      const response = await apiService.getVotingResults(session.id);
      
      // Estructurar los datos para el modal
      const detailedResults = response.data.data || response.data;
      
      const fullSession = {
        ...session,
        // Fusionar los datos del endpoint con la sesión original
        ...detailedResults,
        // Mantener las propiedades originales de la sesión
        id: session.id,
        question: session.question,
        description: session.description,
        status: session.status,
        type: session.type
      };

      setSelectedSession(fullSession);
      
      // ✅ Guardar última votación vista en localStorage
      localStorage.setItem('lastViewedVotingSessionId', session.id);
      console.log('✅ Última votación vista guardada:', session.id);
    } catch (err) {
      console.error('Error loading voting details:', err);
      // Mostrar el modal incluso si no podemos cargar detalles adicionales
      const fallbackSession = {
        ...session,
        results: null
      };
      setSelectedSession(fallbackSession);
      localStorage.setItem('lastViewedVotingSessionId', session.id);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <LoadingState />
    );
  }

  if (!hasRequestedLoad) {
    return (
      <div className="text-center py-6">
        <button
          onClick={() => setHasRequestedLoad(true)}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
        >
          Ver historial
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${shellCardClassName} border-red-200 bg-red-50 p-4`}>
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadVotingHistory}
          className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  if (votingSessions.length === 0) {
    return (
      <div className={`${shellCardClassName} py-8 text-center`}>
        <ChartBarIcon className="mx-auto mb-3 h-12 w-12 text-slate-400" />
        <p className="text-slate-600">No hay votaciones completadas aún</p>
        <p className="mt-2 text-xs text-slate-500">Buscando sesiones con estado 'completed' o 'cancelled'</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Mostrando {votingSessions.length} votación{votingSessions.length !== 1 ? 'es' : ''}
      </p>
      
      {votingSessions.map((session) => (
        <div key={session.id} className={`${shellCardClassName} p-4`}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3">
                  {getStatusIcon(session.status)}
                  <div className="min-w-0 flex-1">
                    <h4 className="break-words font-medium text-slate-900">
                      {session.question}
                    </h4>
                    {session.agendaItem && (
                      <p className="mt-1 break-words text-sm text-blue-600">
                        Punto de agenda: {session.agendaItem.title}
                      </p>
                    )}
                    <p className="mt-1 break-words text-sm text-slate-600">
                      {session.description}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
                      <span>
                        {formatDate(getRelevantDate(session))}
                      </span>
                      {votedSessionIds.has(session.id) && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          Votaste
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-wrap items-center gap-2 justify-start md:w-auto justify-end md:pt-1">
                <span className={`rounded-full px-2 py-1 text-xs ${getStatusColor(session.status)}`}>
                  {getStatusText(session.status)}
                </span>
                {userRole === 'admin' && (
                  <button
                    onClick={() => handleViewDetails(session)}
                    disabled={loadingDetails}
                    className="flex items-center rounded-lg px-3 py-1 text-sm text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800 disabled:opacity-50"
                    title="Ver detalles completos"
                  >
                    {loadingDetails ? (
                      <div className="mr-1 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    ) : (
                      <EyeIcon className="mr-1 h-4 w-4" />
                    )}
                    Ver detalles
                  </button>
                )}
              </div>
            </div>

            {session.status === 'draft' ? (
              <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800">
                  Esta votación aún no ha sido iniciada por el administrador
                </p>
              </div>
            ) : session.status === 'active' ? (
              <div className="w-full rounded-xl border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">
                  Votación en curso. Ve a "Participar en Votación" para emitir tu voto
                </p>
              </div>
            ) : session.status === 'completed' ? (
              <div className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3">
                {session.optionSummaries && session.optionSummaries.length > 0 ? (
                  <div className="w-full space-y-2">
                    <h5 className="text-sm font-medium text-slate-900">Resultados:</h5>
                    <div className="w-full space-y-2">
                      {(() => {
                        const maxPercentage = Math.max(...session.optionSummaries.map((s) => Number((s.percentageByCoefficient ?? s.percentage) || 0)), 0);
                        return session.optionSummaries.map((summary) => {
                          const percentage = Number((summary.percentageByCoefficient ?? summary.percentage) || 0);
                          const voteCount = Number(summary.votes || 0);
                          const coefficient = Number(summary.coefficient || 0);
                          const totalCoefficient = Number(session.totalCoefficient || 0);
                          const isWinner = maxPercentage > 0 && percentage === maxPercentage;
                          const barWidth = percentage <= 0 ? 0 : percentage;

                          return (
                            <div key={summary.optionId} className="w-full rounded-xl border border-slate-200 bg-white p-3">
                              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <span className={`text-sm font-medium ${isWinner ? 'text-green-700' : 'text-slate-700'}`}>
                                  {summary.optionText}
                                  {isWinner && session.status === 'completed' && ' ✓'}
                                </span>
                                <div className="text-left sm:text-right">
                                  <div className={`text-base font-bold ${isWinner ? 'text-green-600' : 'text-blue-600'}`}>
                                    {percentage.toFixed(1)}%
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    {voteCount} voto{voteCount !== 1 ? 's' : ''}
                                    {coefficient > 0 && totalCoefficient > 0 ? ` · ${coefficient.toFixed(10)} coef.` : ''}
                                  </div>
                                </div>
                              </div>
                              <div className="h-2 w-full rounded-full bg-slate-200">
                                <div
                                  className={`h-2 rounded-full transition-all duration-500 ${
                                    isWinner && session.status === 'completed'
                                      ? 'bg-gradient-to-r from-green-500 to-green-600'
                                      : 'bg-gradient-to-r from-blue-400 to-blue-500'
                                  }`}
                                  style={{ width: `${barWidth}%` }}
                                  title={`${percentage.toFixed(1)}%`}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700">Sin resultados disponibles para esta votación.</p>
                )}
              </div>
            ) : (
              <div className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm text-slate-700">
                  Esta votación no finalizó con resultados publicados.
                </p>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Modal de detalles */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="m-4 max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-900">
                Detalles de la Votación
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-slate-900">Pregunta:</h4>
                  <p className="text-slate-700">{selectedSession.question}</p>
                </div>
                
                {selectedSession.description && (
                  <div>
                    <h4 className="font-medium text-slate-900">Descripción:</h4>
                    <p className="text-slate-700">{selectedSession.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-slate-900">Estado:</h4>
                    <span className={`inline-block text-xs px-2 py-1 rounded ${getStatusColor(selectedSession.status)}`}>
                      {getStatusText(selectedSession.status)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Tipo:</h4>
                    <p className="capitalize text-slate-700">{selectedSession.type.replace('_', ' ')}</p>
                  </div>
                </div>

                {/* Estadísticas con colores */}
                <div>
                  <h4 className="mb-2 font-medium text-slate-900">Estadísticas:</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {(() => {
                          const votedCoefficient = parseFloat((selectedSession as any).votedCoefficient ?? 0) || 0;
                          return votedCoefficient.toFixed(10) + '%';
                        })()}
                      </div>
                      <div className="text-sm text-blue-700">Coeficiente total de votos</div>
                    </div>
                  </div>
                </div>

                {/* Resumen estadístico mejorado */}
                    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <h5 className="mb-3 font-medium text-slate-900">Resumen de Resultados:</h5>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <span className="text-slate-600">Opción ganadora:</span>
                          <br />
                          <span className="font-semibold text-green-700">
                            {(() => {
                              if (!((selectedSession as any).results && Array.isArray((selectedSession as any).results) && (selectedSession as any).results.length > 0)) {
                                return 'N/A';
                              }
                              
                              const results = (selectedSession as any).results;
                              const maxCoefficient = Math.max(...results.map((r: any) => parseFloat(r.coefficient || 0)));
                              const winners = results.filter((r: any) => parseFloat(r.coefficient || 0) === maxCoefficient);
                              
                              // Si hay empate, mostrar todas las opciones empatadas
                              if (winners.length > 1) {
                                return `Empate: ${winners.map((w: any) => w.option).join(', ')}`;
                              }
                              
                              // Si hay un único ganador
                              return winners[0]?.option || 'N/A';
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>

                {/* Resultados detallados */}
                {(selectedSession as any).results && Array.isArray((selectedSession as any).results) && (
                  <div>
                    <h4 className="mb-3 font-medium text-slate-900">Resultados por Opción:</h4>
                    
                    {/* Gráfico de barras con detalles mejorados */}
                    <div className="space-y-4">
                      {(() => {
                        const resultsArray = (selectedSession as any).results as any[];
                        const totalCoefficient = resultsArray.reduce(
                          (sum, item) => sum + (parseFloat(item.coefficient || 0) || 0),
                          0
                        );

                        return resultsArray.map((result: any) => {
                          const coefficient = parseFloat(result.coefficient || 0) || 0;
                          const percentageByCoefficient = parseFloat(result.percentageByCoefficient || 0) || 0;
                          const barWidth = percentageByCoefficient <= 0 ? 0 : percentageByCoefficient;

                          return (
                          <div key={result.option} className="rounded-xl border border-slate-200 p-4">
                            {/* Encabezado con opción */}
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="text-lg font-medium text-slate-900">{result.option}</h5>
                            </div>

                            {/* Porcentaje por coeficiente */}
                            <div className="text-center p-3 bg-blue-50 rounded mb-3">
                              <div className="text-2xl font-bold text-blue-600">
                                {percentageByCoefficient.toFixed(1)}%
                              </div>
                              <div className="mt-1 text-sm text-slate-600">
                                Por coeficiente total
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                ({coefficient.toFixed(10)} de {totalCoefficient.toFixed(10)})
                              </div>
                            </div>

                            {/* Barra visual basada en coeficiente */}
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-slate-600">Representación por coeficiente:</div>
                              <div className="h-4 w-full rounded-full bg-slate-200">
                                <div 
                                  className={`bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end ${barWidth > 5 ? 'pr-2' : ''}`}
                                  style={{ width: `${barWidth}%` }}
                                  title={`${percentageByCoefficient.toFixed(1)}% del coeficiente total`}
                                >
                                  {barWidth > 10 && (
                                    <span className="text-white text-xs font-bold">{percentageByCoefficient.toFixed(0)}%</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Detalles adicionales para administradores */}
                    {userRole === 'admin' && (selectedSession as any).voterDetails && (
                      <div className="mt-4">
                        <h5 className="mb-3 font-medium text-slate-900">Detalles de Votación (Solo Admin):</h5>
                        <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-slate-700">Votante</th>
                                {/* <th className="px-3 py-2 text-left text-gsray-700 font-medium">Apartamento</th> */}
                                <th className="px-3 py-2 text-left font-medium text-slate-700">Voto</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-700">Coeficiente</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-700">Fecha</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {(selectedSession as any).voterDetails.map((voter: any, index: number) => (
                                <tr key={index} className="hover:bg-slate-50">
                                  <td className="px-3 py-2 text-slate-900">
                                    {voter.userName}
                                  </td>
                                  {/* <td className="px-3 py-2 text-gray-600">
                                    {voter.apartment}
                                  </td> */}
                                  <td className="px-3 py-2">
                                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      {voter.vote}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {parseFloat(voter.coefficient).toFixed(10)}%
                                  </td>
                                  <td className="px-3 py-2 text-slate-500">
                                    {new Date(voter.votedAt).toLocaleString('es-ES')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedSession(null)}
                  className="rounded-xl bg-slate-600 px-4 py-2 text-white transition-colors hover:bg-slate-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VotingHistory;